<template>
    <div class="w-full flex flex-col">
        <loading-placeholder v-if="isLoading"
                             class="mb-32"
                             type="box"
                             size="big"
                             :amount="1"></loading-placeholder>

        <loading-placeholder v-if="isLoading"
                             type="box"
                             size="big"
                             :amount="9"></loading-placeholder>

        <pagination
            :data="pagination"
            :on-each-side="2"
            class="mt-4 mb-16"
            @paginateTo="getJobs"
        />

        <ul class="space-y-24">
            <job
                v-if="this.activeState === 'active' && spontaneous"
                :job="spontaneous"
            />
            <template v-if="hasJobs">
                <job v-for="job in jobs" :key="job.id" :draggable="activeState === 'active'" :job="job"/>
            </template>
        </ul>

        <empty-state v-if="!hasJobs && !isLoading"
                     :title="$t('jobs.empty_state.title')"
                     :description="$t('jobs.empty_state.description')"
                     @click="$emit('clear')">
        </empty-state>

        <pagination :data="pagination"
                    :on-each-side="2"
                    class="my-24"
                    @paginateTo="getJobs"
        ></pagination>

    </div>
</template>

<script>
import JobComponent from './Job.vue';
import draggable from 'vuedraggable';
import _debounce from 'lodash/debounce';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import moment from 'moment';
import Job from '../models/Job';
import Pagination from './navigation/Pagination.vue';
import EmptyState from './ui/EmptyState.vue';
import LoadingPlaceholder from './ui/LoadingPlaceholder.vue';
import {mapStores} from "pinia";
import {useJobsStore} from "../state/modules/jobs";
import {useAuthenticationStore} from "../state/modules/authentication";
import {useMainStore} from "../state/store";
import {useNotificationsStore} from "../state/modules/notifications";
import {mapRepos} from "pinia-orm";

export default {
    components: {
        LoadingPlaceholder,
        EmptyState,
        Pagination,
        'job': JobComponent,
        'draggable': draggable,
    },
    props: {
        'activeState': {
            type: String,
            validator: value => ['active', 'archived'].indexOf(value) !== -1,
        },
        filter: {
            type: String,
            required: false,
            default: '',
        },
        createDateStartFilter: [Number, String],
        createDateEndFilter: [Number, String],
        publishDateStartFilter: [Number, String],
        publishDateEndFilter: [Number, String],
        archiveDateStartFilter: [Number, String],
        archiveDateEndFilter: [Number, String],
        jobReasonAcceptedFilter: [Number, String],
        jobReasonRejectedFilter: [Number, String],
        regionFilter: [Number, String],
        locationFilter: [Number, String],
        userFilter: [Number, String],
        tagsFilter: Array
    },
    data: function () {
        return {
            drag: false,
            preventFetch: false,
        };
    },
    computed: {
        ...mapStores(useJobsStore, useAuthenticationStore, useMainStore, useNotificationsStore),
        ...mapRepos({jobRepo: Job}),
        fetchingJobs() {
            return this.jobsModuleStore.fetchingJobs;
        },
        allJobsFetched() {
            return this.jobsModuleStore.allJobsFetched;
        },
        paginatedResult() {
            return this.jobsModuleStore.paginatedResult;
        },
        currentCompanyId() {
            return this.authenticationStore.currentCompanyId;
        },
        locale() {
            return this.mainStore.locale;
        },
        jobs: {
            get() {
                const isArchive = this.activeState === 'archived';
                let jobs = this.jobRepo
                    .where('company_id', this.currentCompanyId)
                    .where('is_spontaneous', 0)
                    .where('archived', isArchive ? (archived => archived !== null) : null)
                    .with('stages')
                    .with('attachments')
                    .with('filters')
                    .with('subscriptions')
                    .with('location')
                    .with('responsible_user')
                    .get();

                if (isArchive) {
                    return jobs.sort((a, b) => moment(a.archived).isBefore(b.archived) ? 1 : -1);
                } else {
                    return jobs.sort((a, b) => a.sequence > b.sequence ? 1 : -1);
                }
            },
            set(jobs) {
                this.jobsModuleStore.resort(jobs)
                    .then(result => {
                        this.notificationsModuleStore[ADD_NOTIFICATION]({
                            type: 'success',
                            message: this.$t('jobs.resort_success'),
                        });
                    });
            },
        },
        pagination() {
            return this.paginatedResult;
        },
        jobCount() {
            return this.jobs.length;
        },
        hasJobs() {
            return this.jobCount >= 1;
        },
        isLoading() {
            return this.fetchingJobs && !this.hasJobs;
        },
        allJobsLoaded() {
            return this.allJobsFetched[this.activeState];
        },
        spontaneous() {
            return this.jobRepo
                .where('company_id', this.currentCompanyId)
                .where('is_spontaneous', 1)
                .with('stages')
                .with('attachments')
                .with('filters')
                .with('subscriptions')
                .first();
        },
        dragOptions() {
            return {
                animation: 200,
                group: 'items',
                disabled: false,
                ghostClass: 'ghost',
            };
        },
    },
    methods: {
        currentPage() {
            const urlParams = new URLSearchParams(window.location.search);
            let page = urlParams.get('page');

            if (!page) {
                page = 1;
            }

            return page;
        },
        getJobs(pageNumber = 1) {
            if (this.preventFetch) {
                return;
            }

            //There is a problem that the watcher detects a change in the route and calls this function again
            //We need the watcher to detect when a user uses the back button in the browser
            this.preventFetch = true;

            let filters = {
                title: this.filter,
                locale: this.locale,
                company_location_id: this.locationFilter,
                responsible_user_id: this.userFilter,
                created_at_start: this.createDateStartFilter ? moment(this.createDateStartFilter).format('YYYY-MM-DD') : null,
                created_at_end: this.createDateEndFilter ? moment(this.createDateEndFilter).format('YYYY-MM-DD') : null,
                published_at_start: this.publishDateStartFilter ? moment(this.publishDateStartFilter).format('YYYY-MM-DD') : null,
                published_at_end: this.publishDateEndFilter ? moment(this.publishDateEndFilter).format('YYYY-MM-DD') : null,
                archived_at_start: this.archiveDateStartFilter ? moment(this.archiveDateStartFilter).format('YYYY-MM-DD') : null,
                archived_at_end: this.archiveDateEndFilter ? moment(this.archiveDateEndFilter).format('YYYY-MM-DD') : null,
                region: this.regionFilter,
                job_reason_accepted: this.jobReasonAcceptedFilter,
                job_reason_rejected: this.jobReasonRejectedFilter,
                tag_id: this.tagsFilter ? this.tagsFilter.join(',') : this.tagsFilter
            };

            if (this.locationFilter === 'remote') {
                delete (filters.company_location_id);
                filters.remote = true;
            }

            this.jobsModuleStore.fetch({
                filters,
                activeState: this.activeState,
                page: pageNumber,
            }).then(result => {
                this.preventFetch = false;
            });
        },
    },
    watch: {
        filter: _debounce(function (newVal, oldVal) {
            if (newVal.length > 1 ||
                (newVal !== oldVal && newVal === '')) {
                this.jobsModuleStore.clear();
            }
        }, 500),
        locationFilter() {
            this.jobsModuleStore.clear();
        },
        userFilter() {
            this.jobsModuleStore.clear();
        },
        tagsFilter() {
            this.jobsModuleStore.clear();
        },
        '$route'(route) {
            // react to route changes, check if you go back with browser
            if (this.pagination.meta) {
                if (parseInt(this.currentPage()) !== this.pagination.meta.current_page) {
                    this.getJobs(this.currentPage());
                }
            }
        },
    },
    mounted() {
        this.getJobs(this.currentPage());
    },
};
</script>
